import React from 'react';
import { IntlShape } from 'react-intl';

import { PlayerContentType } from '@/scenarios/legacy-player/components/feature-player/types/content';

import { Option } from '../OptionList/OptionList.types';
import { SubtitlesRatio } from '../Subtitles/Subtitles.types';
import { Icon, IconSize, IconType } from '../Icon';

import { PlaybackRate, PLAYBACK_RATES, SettingsProps, SettingsScreen } from './Settings.types';
import { getSubtitlesSizeOptions, qualityAliases } from './Settings.config';

interface GetSettingsOptionsParams {
    intl: IntlShape;
    currentQuality: Option<string> | null;
    currentSubtitlesSize?: Option<SubtitlesRatio>;
    currentRate: Option<PlaybackRate> | null;
    iconClassName: string;
    contentType: PlayerContentType | null;
}

export const getSettingsOptions = ({
    intl,
    currentQuality,
    currentSubtitlesSize,
    currentRate,
    iconClassName,
    contentType,
}: GetSettingsOptionsParams): readonly Option<SettingsScreen>[] => {
    const options = [];

    const subtitlesSizeOption = {
        name: intl.formatMessage({
            id: 'components.PlayerSkin.subtitles-size-option-name.3a9dd5e1d7c003ba046106646aa40aa4',
            defaultMessage: 'Subtitle size',
            description: 'Размер субтитров в настройках плеера',
        }),
        value: SettingsScreen.SUBTITLES_SIZE,
        icon: (
            <Icon
                type={IconType.SubtitlesSettingsSize}
                size={IconSize.SubtitlesSettingsSize}
                className={iconClassName}
            />
        ),
        note: currentSubtitlesSize?.name,
        isInlineNote: true,
    };

    options.push(subtitlesSizeOption);

    const qualityOption = {
        name: intl.formatMessage({
            id: 'components.PlayerSkin.quality-option-name.571094bb27864b600d8e6b561a137a55',
            defaultMessage: 'Quality',
            description: 'Качество видео в настройках плеера',
        }),
        value: SettingsScreen.QUALITY,
        icon: <Icon type={IconType.Settings} size={IconSize.Option} className={iconClassName} />,
        note: currentQuality
            ? `${currentQuality.name}${currentQuality.note ? ` (${currentQuality.note})` : ''}`
            : undefined,
        isInlineNote: true,
    };

    options.push(qualityOption);

    if (contentType !== PlayerContentType.CHANNEL && currentRate) {
        const rateOption = {
            name: intl.formatMessage({
                id: 'components.PlayerSkin.rate-option-name.44877c6aa8e93fa5a91c9361211464fb',
                defaultMessage: 'Speed',
                description: 'Скорость воспроизведения в настройках плеера',
            }),
            value: SettingsScreen.RATE,
            icon: <Icon type={IconType.Rate} size={IconSize.Option} className={iconClassName} />,
            note: currentRate ? currentRate.name : undefined,
            isInlineNote: true,
        };

        options.push(rateOption);
    }

    return options;
};

export const getQualityOptions = (
    videoTracks: SettingsProps['videoTracks'],
    autoQualityOption: Option<string>
): readonly Option<string>[] =>
    videoTracks.length > 1
        ? [autoQualityOption].concat(
              videoTracks.map((track) =>
                  track.name in qualityAliases
                      ? { ...track, isInlineNote: true, note: qualityAliases[track.name] }
                      : track
              )
          )
        : [{ ...autoQualityOption, disabled: true }];

export const rangeByStep = (min: number, max: number): PlaybackRate[] =>
    PLAYBACK_RATES.filter((rate) => rate >= min && rate <= max);

export const getCurrentSubtitlesSizeOption = (
    intl: IntlShape,
    subtitlesRatio: SubtitlesRatio
): Option<SubtitlesRatio> | undefined => {
    const subtitlesSizeOptions = getSubtitlesSizeOptions(intl);
    return (
        subtitlesSizeOptions.find(({ value }) => value === subtitlesRatio) ||
        subtitlesSizeOptions[1]
    );
};
